import React, { useState, useEffect, useContext } from 'react';
import {
  XMarkIcon,
  ShieldCheckIcon,
  UserIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  MagnifyingGlassIcon,
  ChartBarIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { AuthContext } from '../../App';
import ConfirmationModal from '../ConfirmationModal';

interface MemberAccess {
  mg_nr: number;
  name: string;
  roles: {
    vorstand: boolean;
    trainer: boolean;
    kassenpruefer: boolean;
  };
  access: {
    document_count: number;
    dog_document_count: number;
    total_count: number;
  };
  documents: Array<{
    id: number;
    name: string;
  }>;
}

interface DocumentDetail {
  id: number;
  name: string;
  created_at: string;
  size?: number;
  type?: string;
  tags: string[];
  creator?: string;
  access: {
    start_date: string | null;
    expiration_date: string | null;
    is_active: boolean;
  };
}

interface DogDocumentDetail {
  id: number;
  name: string;
  created_at: string;
  type?: string;
  dog_name: string;
  access: {
    start_date: string | null;
    expiration_date: string | null;
    is_active: boolean;
  };
}

interface MemberDetails {
  member: {
    mg_nr: number;
    name: string;
    email: string;
    roles: {
      vorstand: boolean;
      trainer: boolean;
      kassenpruefer: boolean;
    };
  };
  documents: DocumentDetail[];
  dog_documents: DogDocumentDetail[];
  stats: {
    active_documents: number;
    expired_documents: number;
    active_dog_documents: number;
    expired_dog_documents: number;
    total_documents: number;
  };
}

interface Stats {
  total_members: number;
  members_with_access: number;
  total_document_accesses: number;
  total_dog_document_accesses: number;
}

interface DocumentAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DocumentAccessModal: React.FC<DocumentAccessModalProps> = ({ isOpen, onClose }) => {
  const { BASE_URL } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<MemberAccess[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [selectedMember, setSelectedMember] = useState<number | null>(null);
  const [memberDetails, setMemberDetails] = useState<MemberDetails | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<'all' | 'vorstand' | 'trainer' | 'kassenpruefer'>('all');
  const [sortBy, setSortBy] = useState<'name' | 'documents'>('name');

  // Grant Access Form State
  const [showGrantForm, setShowGrantForm] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState<number[]>([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [granting, setGranting] = useState(false);
  const [grantResult, setGrantResult] = useState<{
    success: boolean;
    message: string;
    details?: any;
  } | null>(null);

  // Revoke Access Form State
  const [showRevokeForm, setShowRevokeForm] = useState(false);
  const [selectedMembersRevoke, setSelectedMembersRevoke] = useState<number[]>([]);
  const [fromDateRevoke, setFromDateRevoke] = useState('');
  const [toDateRevoke, setToDateRevoke] = useState('');
  const [revoking, setRevoking] = useState(false);
  const [revokeResult, setRevokeResult] = useState<{
    success: boolean;
    message: string;
    details?: any;
  } | null>(null);

  // Single Revoke Confirmation Modal State
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [revokeData, setRevokeData] = useState<{
    documentId: number;
    memberId: number;
    documentName: string;
  } | null>(null);
  const [revokingDocument, setRevokingDocument] = useState(false);

  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const timer = setTimeout(() => setIsVisible(true), 10);
      return () => { document.body.style.overflow = ''; clearTimeout(timer); };
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      fetchOverview();
    }
  }, [isOpen]);

  const fetchOverview = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('jwt_token');
      const baseUrl = BASE_URL || localStorage.getItem('club_url') || process.env.REACT_APP_API_URL || 'http://localhost:4000';

      const response = await fetch(`${baseUrl}/api/documents/access-overview`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Fehler beim Laden der Übersicht');
      }

      const data = await response.json();
      setOverview(data.overview);
      setStats(data.stats);
    } catch (error) {
      console.error('Fehler beim Laden der Dokumentenrechte:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMemberDetails = async (memberId: number) => {
    setLoadingDetails(true);
    try {
      const token = localStorage.getItem('jwt_token');
      const baseUrl = BASE_URL || localStorage.getItem('club_url') || process.env.REACT_APP_API_URL || 'http://localhost:4000';

      const response = await fetch(`${baseUrl}/api/documents/access-details/${memberId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Fehler beim Laden der Details');
      }

      const data = await response.json();
      setMemberDetails(data);
    } catch (error) {
      console.error('Fehler beim Laden der Mitglieds-Details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleMemberClick = (memberId: number) => {
    if (selectedMember === memberId) {
      setSelectedMember(null);
      setMemberDetails(null);
    } else {
      setSelectedMember(memberId);
      fetchMemberDetails(memberId);
    }
  };

  const handleGrantAccess = async () => {
    if (selectedMembers.length === 0) {
      setGrantResult({
        success: false,
        message: 'Bitte wählen Sie mindestens ein Mitglied aus.'
      });
      return;
    }

    if (!fromDate || !toDate) {
      setGrantResult({
        success: false,
        message: 'Bitte geben Sie einen Zeitraum an.'
      });
      return;
    }

    setGranting(true);
    setGrantResult(null);

    try {
      const token = localStorage.getItem('jwt_token');
      const baseUrl = BASE_URL || localStorage.getItem('club_url') || process.env.REACT_APP_API_URL || 'http://localhost:4000';

      const response = await fetch(`${baseUrl}/api/documents/grant-access`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          member_ids: selectedMembers,
          from_date: fromDate,
          to_date: toDate,
          start_date: startDate || null,
          expiration_date: expirationDate || null
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setGrantResult({
          success: true,
          message: `Erfolgreich! ${data.granted} Rechte vergeben für ${data.total_documents} Dokumente an ${data.total_members} Mitglieder.`,
          details: data
        });

        // Reset form
        setSelectedMembers([]);
        setFromDate('');
        setToDate('');
        setStartDate('');
        setExpirationDate('');

        // Refresh overview
        fetchOverview();
      } else {
        setGrantResult({
          success: false,
          message: data.error || 'Fehler beim Vergeben der Rechte',
          details: data
        });
      }
    } catch (error) {
      console.error('Fehler beim Vergeben der Rechte:', error);
      setGrantResult({
        success: false,
        message: 'Netzwerkfehler beim Vergeben der Rechte'
      });
    } finally {
      setGranting(false);
    }
  };

  const toggleMemberSelection = (memberId: number) => {
    setSelectedMembers(prev =>
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const selectAllMembers = () => {
    setSelectedMembers(filteredMembers.map(m => m.mg_nr));
  };

  const deselectAllMembers = () => {
    setSelectedMembers([]);
  };

  // Revoke Access Functions
  const handleRevokeAccess = async () => {
    if (selectedMembersRevoke.length === 0) {
      setRevokeResult({
        success: false,
        message: 'Bitte wählen Sie mindestens ein Mitglied aus.'
      });
      return;
    }

    if (!fromDateRevoke || !toDateRevoke) {
      setRevokeResult({
        success: false,
        message: 'Bitte geben Sie einen Zeitraum an.'
      });
      return;
    }

    setRevoking(true);
    setRevokeResult(null);

    try {
      const token = localStorage.getItem('jwt_token');
      const baseUrl = BASE_URL || localStorage.getItem('club_url') || process.env.REACT_APP_API_URL || 'http://localhost:4000';

      const response = await fetch(`${baseUrl}/api/documents/revoke-access`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          member_ids: selectedMembersRevoke,
          from_date: fromDateRevoke,
          to_date: toDateRevoke
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setRevokeResult({
          success: true,
          message: `Erfolgreich! ${data.revoked} Rechte entzogen für ${data.total_documents} Dokumente von ${data.total_members} Mitgliedern.`,
          details: data
        });

        // Reset form
        setSelectedMembersRevoke([]);
        setFromDateRevoke('');
        setToDateRevoke('');

        // Refresh overview
        fetchOverview();
      } else {
        setRevokeResult({
          success: false,
          message: data.error || 'Fehler beim Entziehen der Rechte',
          details: data
        });
      }
    } catch (error) {
      console.error('Fehler beim Entziehen der Rechte:', error);
      setRevokeResult({
        success: false,
        message: 'Netzwerkfehler beim Entziehen der Rechte'
      });
    } finally {
      setRevoking(false);
    }
  };

  const toggleMemberSelectionRevoke = (memberId: number) => {
    setSelectedMembersRevoke(prev =>
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const selectAllMembersRevoke = () => {
    setSelectedMembersRevoke(filteredMembers.map(m => m.mg_nr));
  };

  const deselectAllMembersRevoke = () => {
    setSelectedMembersRevoke([]);
  };

  // Einzelnes Dokumentenrecht entziehen - Modal öffnen
  const handleRevokeSingleAccess = (documentId: number, memberId: number, documentName: string) => {
    setRevokeData({ documentId, memberId, documentName });
    setShowRevokeConfirm(true);
  };

  // Bestätigung des Rechte-Entzugs
  const confirmRevokeSingleAccess = async () => {
    if (!revokeData) return;

    setRevokingDocument(true);

    try {
      const token = localStorage.getItem('jwt_token');
      const baseUrl = BASE_URL || localStorage.getItem('club_url') || process.env.REACT_APP_API_URL || 'http://localhost:4000';

      const response = await fetch(`${baseUrl}/api/documents/revoke-single-access`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          document_id: revokeData.documentId,
          member_id: revokeData.memberId
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Refresh member details and overview
        fetchMemberDetails(revokeData.memberId);
        fetchOverview();
        setShowRevokeConfirm(false);
        setRevokeData(null);
      } else {
        alert(data.error || 'Fehler beim Entziehen des Rechts');
      }
    } catch (error) {
      console.error('Fehler beim Entziehen des Rechts:', error);
      alert('Netzwerkfehler beim Entziehen des Rechts');
    } finally {
      setRevokingDocument(false);
    }
  };

  const getRoleBadges = (roles: MemberAccess['roles']) => {
    const badges = [];
    if (roles.vorstand) {
      badges.push(
        <span key="vorstand" className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-800">
          Vorstand
        </span>
      );
    }
    if (roles.trainer) {
      badges.push(
        <span key="trainer" className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-info-50 dark:bg-info-900/20 text-info-700 dark:text-info-300 border border-info-200 dark:border-info-800">
          Trainer
        </span>
      );
    }
    if (roles.kassenpruefer) {
      badges.push(
        <span key="kassenpruefer" className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-success-50 dark:bg-success-900/20 text-success-700 dark:text-success-300 border border-success-200 dark:border-success-800">
          Kassenprüfer
        </span>
      );
    }
    return badges;
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatSize = (bytes: number | undefined) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Filter and sort members
  const filteredMembers = overview
    .filter(member => {
      // Search filter
      if (searchTerm && !member.name.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      // Role filter
      if (filterRole !== 'all') {
        if (filterRole === 'vorstand' && !member.roles.vorstand) return false;
        if (filterRole === 'trainer' && !member.roles.trainer) return false;
        if (filterRole === 'kassenpruefer' && !member.roles.kassenpruefer) return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'name') {
        return a.name.localeCompare(b.name);
      } else {
        return b.access.total_count - a.access.total_count;
      }
    });

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-6xl bg-white dark:bg-[#1c2432] border-t sm:border border-gray-200 dark:border-gray-700 rounded-t-2xl sm:rounded-2xl max-h-[90vh] flex flex-col shadow-2xl animate-slide-up sm:animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="w-9 h-9 rounded-xl bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-400 grid place-items-center flex-shrink-0">
            <ShieldCheckIcon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-gray-900 dark:text-white truncate">Dokumentenrechte-Verwaltung</h3>
            <p className="text-[11px] text-gray-500 dark:text-gray-400">Übersicht über Zugriffsrechte aller Mitglieder</p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 grid place-items-center rounded-lg hover:bg-gray-100 dark:hover:bg-white/5 text-gray-500 dark:text-gray-400 flex-shrink-0"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Statistics Bar */}
        {stats && (
          <div className="bg-gray-50/50 dark:bg-gray-800/40 border-b border-gray-200 dark:border-gray-700 px-6 py-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                {
                  label: 'Mitglieder',
                  value: stats.total_members,
                  sub: 'Gesamt im Verein',
                  Icon: UserIcon,
                  iconBg: 'bg-info-50 dark:bg-info-900/20',
                  iconColor: 'text-info-600 dark:text-info-400',
                },
                {
                  label: 'Mit Zugriff',
                  value: stats.members_with_access,
                  sub: stats.total_members > 0
                    ? `${Math.round((stats.members_with_access / stats.total_members) * 100)}% von Gesamt`
                    : '—',
                  Icon: CheckCircleIcon,
                  iconBg: 'bg-success-50 dark:bg-success-900/20',
                  iconColor: 'text-success-600 dark:text-success-400',
                },
                {
                  label: 'Dokumente',
                  value: stats.total_document_accesses,
                  sub: 'Vergebene Rechte',
                  Icon: DocumentTextIcon,
                  iconBg: 'bg-primary-50 dark:bg-primary-900/20',
                  iconColor: 'text-primary-600 dark:text-primary-400',
                },
                {
                  label: 'Hundedokumente',
                  value: stats.total_dog_document_accesses,
                  sub: 'Vergebene Rechte',
                  Icon: ChartBarIcon,
                  iconBg: 'bg-warning-50 dark:bg-warning-900/20',
                  iconColor: 'text-warning-600 dark:text-warning-400',
                },
              ].map(({ label, value, sub, Icon, iconBg, iconColor }) => (
                <div
                  key={label}
                  className="bg-white dark:bg-[#1c2432] rounded-xl border border-gray-200 dark:border-gray-700 p-4"
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      {label}
                    </p>
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${iconBg}`}>
                      <Icon className={`h-4 w-4 ${iconColor}`} />
                    </div>
                  </div>
                  <p className="text-2xl font-bold tabular-nums text-gray-900 dark:text-white mt-2">
                    {value}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {sub}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Toolbar (Actions) */}
        <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 pt-4 pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              <ShieldCheckIcon className="h-4 w-4" />
              Aktionen
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => {
                  setShowGrantForm(!showGrantForm);
                  if (!showGrantForm) setShowRevokeForm(false);
                }}
                className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                  showGrantForm
                    ? 'bg-primary-600 text-white hover:bg-primary-700 border border-primary-600'
                    : 'bg-primary-600 text-white hover:bg-primary-700 border border-primary-600'
                }`}
              >
                <PlusIcon className="h-4 w-4" />
                Rechte vergeben
              </button>
              <button
                onClick={() => {
                  setShowRevokeForm(!showRevokeForm);
                  if (!showRevokeForm) setShowGrantForm(false);
                }}
                className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium transition-colors border ${
                  showRevokeForm
                    ? 'bg-error-50 dark:bg-error-900/20 text-error-700 dark:text-error-300 border-error-300 dark:border-error-800'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <XMarkIcon className="h-4 w-4" />
                Rechte entziehen
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-3">
          <div className="flex flex-col sm:flex-row gap-2.5">
            {/* Search */}
            <div className="relative flex-1 min-w-0">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Mitglied suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
              />
            </div>

            {/* Role Filter */}
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value as any)}
              className="px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm text-gray-900 dark:text-white"
            >
              <option value="all">Alle Rollen</option>
              <option value="vorstand">Vorstand</option>
              <option value="trainer">Trainer</option>
              <option value="kassenpruefer">Kassenprüfer</option>
            </select>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm text-gray-900 dark:text-white"
            >
              <option value="name">Sortieren: Name</option>
              <option value="documents">Sortieren: Dokumente</option>
            </select>
          </div>
        </div>

        {/* Grant Access Form */}
        {showGrantForm && (
          <div className="bg-primary-50/40 dark:bg-primary-900/10 border-b border-primary-200 dark:border-primary-900/40 px-6 py-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <ShieldCheckIcon className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                  Dokumentenrechte vergeben
                </h4>
                <button
                  onClick={() => { setShowGrantForm(false); setGrantResult(null); }}
                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>

              <p className="text-xs text-gray-600 dark:text-gray-400">
                Rechte auf alle Dokumente vergeben, die in einem bestimmten Zeitraum erstellt wurden.
              </p>

              {/* Date Ranges */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Dokumente von', val: fromDate, set: setFromDate, required: true },
                  { label: 'Dokumente bis', val: toDate, set: setToDate, required: true },
                  { label: 'Zugriff ab (optional)', val: startDate, set: setStartDate, required: false },
                  { label: 'Zugriff bis (optional)', val: expirationDate, set: setExpirationDate, required: false },
                ].map(f => (
                  <div key={f.label}>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      {f.label}{f.required && <span className="text-error-600"> *</span>}
                    </label>
                    <input
                      type="date"
                      value={f.val}
                      onChange={(e) => f.set(e.target.value)}
                      className="w-full px-2.5 py-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                ))}
              </div>

              {/* Member Selection Info */}
              <div className="flex items-center justify-between bg-white dark:bg-[#1c2432] rounded-lg p-2.5 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 text-sm">
                  <UserIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <span className="font-medium text-gray-900 dark:text-white tabular-nums">
                    {selectedMembers.length}
                  </span>
                  <span className="text-gray-500 dark:text-gray-400">
                    Mitglied{selectedMembers.length !== 1 ? 'er' : ''} ausgewählt
                  </span>
                </div>
                <div className="flex gap-3 text-xs">
                  <button onClick={selectAllMembers} className="text-primary-600 dark:text-primary-400 hover:underline">
                    Alle
                  </button>
                  <button onClick={deselectAllMembers} className="text-gray-500 dark:text-gray-400 hover:underline">
                    Aufheben
                  </button>
                </div>
              </div>

              {/* Submit + Result */}
              <button
                onClick={handleGrantAccess}
                disabled={granting || selectedMembers.length === 0 || !fromDate || !toDate}
                className="w-full px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-lg transition-colors font-medium text-sm disabled:cursor-not-allowed"
              >
                {granting ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Rechte werden vergeben...
                  </span>
                ) : 'Rechte vergeben'}
              </button>

              {grantResult && (
                <div className={`p-2.5 rounded-lg border text-sm ${
                  grantResult.success
                    ? 'bg-success-50 dark:bg-success-900/20 border-success-200 dark:border-success-800 text-success-900 dark:text-success-100'
                    : 'bg-error-50 dark:bg-error-900/20 border-error-200 dark:border-error-800 text-error-900 dark:text-error-100'
                }`}>
                  <p className="font-medium">{grantResult.message}</p>
                  {grantResult.details?.errors > 0 && (
                    <p className="text-xs opacity-80 mt-0.5">{grantResult.details.errors} Fehler aufgetreten</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Revoke Access Form */}
        {showRevokeForm && (
          <div className="bg-error-50/40 dark:bg-error-900/10 border-b border-error-200 dark:border-error-900/40 px-6 py-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <XCircleIcon className="h-4 w-4 text-error-600 dark:text-error-400" />
                  Dokumentenrechte entziehen
                </h4>
                <button
                  onClick={() => { setShowRevokeForm(false); setRevokeResult(null); }}
                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>

              <p className="text-xs text-gray-600 dark:text-gray-400">
                Rechte von allen Dokumenten entziehen, die in einem bestimmten Zeitraum erstellt wurden.
              </p>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Dokumente von', val: fromDateRevoke, set: setFromDateRevoke },
                  { label: 'Dokumente bis', val: toDateRevoke, set: setToDateRevoke },
                ].map(f => (
                  <div key={f.label}>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      {f.label}<span className="text-error-600"> *</span>
                    </label>
                    <input
                      type="date"
                      value={f.val}
                      onChange={(e) => f.set(e.target.value)}
                      className="w-full px-2.5 py-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-error-500 focus:border-error-500"
                    />
                  </div>
                ))}
              </div>

              {/* Member Selection Info */}
              <div className="flex items-center justify-between bg-white dark:bg-[#1c2432] rounded-lg p-2.5 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 text-sm">
                  <UserIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <span className="font-medium text-gray-900 dark:text-white tabular-nums">
                    {selectedMembersRevoke.length}
                  </span>
                  <span className="text-gray-500 dark:text-gray-400">
                    Mitglied{selectedMembersRevoke.length !== 1 ? 'er' : ''} ausgewählt
                  </span>
                </div>
                <div className="flex gap-3 text-xs">
                  <button onClick={selectAllMembersRevoke} className="text-error-600 dark:text-error-400 hover:underline">
                    Alle
                  </button>
                  <button onClick={deselectAllMembersRevoke} className="text-gray-500 dark:text-gray-400 hover:underline">
                    Aufheben
                  </button>
                </div>
              </div>

              {/* Submit + Result */}
              <button
                onClick={handleRevokeAccess}
                disabled={revoking || selectedMembersRevoke.length === 0 || !fromDateRevoke || !toDateRevoke}
                className="w-full px-4 py-2 bg-error-600 hover:bg-error-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-lg transition-colors font-medium text-sm disabled:cursor-not-allowed"
              >
                {revoking ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Rechte werden entzogen...
                  </span>
                ) : 'Rechte entziehen'}
              </button>

              {revokeResult && (
                <div className={`p-2.5 rounded-lg border text-sm ${
                  revokeResult.success
                    ? 'bg-success-50 dark:bg-success-900/20 border-success-200 dark:border-success-800 text-success-900 dark:text-success-100'
                    : 'bg-error-50 dark:bg-error-900/20 border-error-200 dark:border-error-800 text-error-900 dark:text-error-100'
                }`}>
                  <p className="font-medium">{revokeResult.message}</p>
                  {revokeResult.details?.errors > 0 && (
                    <p className="text-xs opacity-80 mt-0.5">{revokeResult.details.errors} Fehler aufgetreten</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="bg-white dark:bg-[#1c2432] rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center gap-3">
                  <div className="w-9 h-9 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3.5 w-1/3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                    <div className="flex gap-1.5">
                      <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded-md animate-pulse" />
                      <div className="h-4 w-14 bg-gray-200 dark:bg-gray-700 rounded-md animate-pulse" />
                    </div>
                  </div>
                  <div className="hidden sm:flex items-center gap-1.5">
                    <div className="h-6 w-12 bg-gray-200 dark:bg-gray-700 rounded-md animate-pulse" />
                    <div className="h-6 w-12 bg-gray-200 dark:bg-gray-700 rounded-md animate-pulse" />
                    <div className="h-6 w-10 bg-gray-200 dark:bg-gray-700 rounded-md animate-pulse" />
                  </div>
                  <div className="w-4 h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredMembers.map((member) => {
                const initials = member.name
                  .split(' ')
                  .map(n => n.charAt(0))
                  .filter(Boolean)
                  .slice(0, 2)
                  .join('')
                  .toUpperCase();
                const hasAccess = member.access.total_count > 0;
                const isExpanded = selectedMember === member.mg_nr;
                const isHighAccess = member.access.total_count >= 10;
                return (
                <div
                  key={member.mg_nr}
                  className={`rounded-xl border overflow-hidden transition-colors ${
                    isExpanded
                      ? 'bg-white dark:bg-[#1c2432] border-primary-300 dark:border-primary-700 ring-1 ring-primary-100 dark:ring-primary-900/40'
                      : isHighAccess
                        ? 'bg-primary-50/40 dark:bg-primary-900/10 border-primary-100 dark:border-primary-900/40 hover:bg-primary-50 dark:hover:bg-primary-900/20'
                        : 'bg-white dark:bg-[#1c2432] border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  {/* Member Row */}
                  <div className="flex items-center">
                    {/* Checkbox for selection */}
                    {(showGrantForm || showRevokeForm) && (
                      <div className="pl-4 pr-1 py-3 flex items-center">
                        <input
                          type="checkbox"
                          checked={
                            showGrantForm
                              ? selectedMembers.includes(member.mg_nr)
                              : selectedMembersRevoke.includes(member.mg_nr)
                          }
                          onChange={(e) => {
                            e.stopPropagation();
                            if (showGrantForm) {
                              toggleMemberSelection(member.mg_nr);
                            } else {
                              toggleMemberSelectionRevoke(member.mg_nr);
                            }
                          }}
                          className={`h-4 w-4 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500 ${
                            showGrantForm
                              ? 'text-primary-600'
                              : 'text-error-600'
                          }`}
                        />
                      </div>
                    )}

                    <button
                      onClick={() => handleMemberClick(member.mg_nr)}
                      className="flex-1 px-4 py-3 flex items-center gap-3 sm:gap-4 text-left"
                    >
                      {/* Avatar */}
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm font-semibold flex-shrink-0 ${
                        hasAccess
                          ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                      }`}>
                        {initials || <UserIcon className="h-5 w-5" />}
                      </div>

                      {/* Name + Roles */}
                      <div className="flex-1 min-w-0">
                        <p className={`font-semibold text-sm truncate ${hasAccess ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'}`}>
                          {member.name}
                        </p>
                        <div className="flex flex-wrap items-center gap-1.5 mt-1">
                          {getRoleBadges(member.roles)}
                          {!hasAccess && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
                              Keine Rechte
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Counter Pills */}
                      <div className="hidden sm:flex items-center gap-1.5 flex-shrink-0">
                        <span
                          title="Dokumente"
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium tabular-nums border ${
                            member.access.document_count > 0
                              ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border-primary-200 dark:border-primary-800'
                              : 'bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-700'
                          }`}
                        >
                          <DocumentTextIcon className="h-3.5 w-3.5" />
                          {member.access.document_count}
                        </span>
                        <span
                          title="Hundedokumente"
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium tabular-nums border ${
                            member.access.dog_document_count > 0
                              ? 'bg-warning-50 dark:bg-warning-900/20 text-warning-700 dark:text-warning-300 border-warning-200 dark:border-warning-800'
                              : 'bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-700'
                          }`}
                        >
                          <ChartBarIcon className="h-3.5 w-3.5" />
                          {member.access.dog_document_count}
                        </span>
                        <span
                          title="Gesamt"
                          className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold tabular-nums ${
                            hasAccess
                              ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
                          }`}
                        >
                          {member.access.total_count}
                        </span>
                      </div>

                      {/* Mobile compact counter */}
                      <div className="sm:hidden flex-shrink-0">
                        <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-bold tabular-nums ${
                          hasAccess
                            ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
                        }`}>
                          {member.access.total_count}
                        </span>
                      </div>

                      {isExpanded ? (
                        <ChevronUpIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      )}
                    </button>
                  </div>

                  {/* Expanded Details */}
                  {selectedMember === member.mg_nr && (
                    <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/40 p-4">
                      {loadingDetails ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary-200 border-t-primary-600"></div>
                        </div>
                      ) : memberDetails ? (
                        <div className="space-y-4">
                          {/* Member Info */}
                          <div className="bg-white dark:bg-[#1c2432] rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                              Mitgliedsinformationen
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
                              <div className="flex items-baseline gap-2">
                                <span className="text-gray-500 dark:text-gray-400 w-12">Name</span>
                                <span className="font-medium text-gray-900 dark:text-white">{memberDetails.member.name}</span>
                              </div>
                              <div className="flex items-baseline gap-2">
                                <span className="text-gray-500 dark:text-gray-400 w-12">E-Mail</span>
                                <span className="font-medium text-gray-900 dark:text-white truncate">{memberDetails.member.email}</span>
                              </div>
                            </div>
                          </div>

                          {/* Statistics */}
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                            {[
                              { label: 'Aktive Dok.', value: memberDetails.stats.active_documents, dot: 'bg-success-500' },
                              { label: 'Abgelaufen', value: memberDetails.stats.expired_documents, dot: 'bg-error-500' },
                              { label: 'Aktive Hundedok.', value: memberDetails.stats.active_dog_documents, dot: 'bg-info-500' },
                              { label: 'Abgel. Hundedok.', value: memberDetails.stats.expired_dog_documents, dot: 'bg-warning-500' },
                              { label: 'Gesamt', value: memberDetails.stats.total_documents, dot: 'bg-primary-500', highlight: true },
                            ].map(s => (
                              <div
                                key={s.label}
                                className={`bg-white dark:bg-[#1c2432] rounded-lg p-3 border ${
                                  s.highlight
                                    ? 'border-primary-200 dark:border-primary-800'
                                    : 'border-gray-200 dark:border-gray-700'
                                }`}
                              >
                                <div className="flex items-center gap-1.5">
                                  <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
                                  <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                    {s.label}
                                  </p>
                                </div>
                                <p className={`text-xl font-bold tabular-nums mt-1 ${
                                  s.highlight
                                    ? 'text-primary-700 dark:text-primary-300'
                                    : 'text-gray-900 dark:text-white'
                                }`}>{s.value}</p>
                              </div>
                            ))}
                          </div>

                          {/* Documents List */}
                          {memberDetails.documents.length > 0 && (
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <h5 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                                  <DocumentTextIcon className="h-4 w-4" />
                                  Dokumente
                                </h5>
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 tabular-nums">
                                  {memberDetails.documents.length}
                                </span>
                              </div>
                              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                                {memberDetails.documents.map((doc) => (
                                  <div
                                    key={doc.id}
                                    className={`p-3 rounded-lg border bg-white dark:bg-[#1c2432] ${
                                      doc.access.is_active
                                        ? 'border-gray-200 dark:border-gray-700'
                                        : 'border-gray-200 dark:border-gray-700 opacity-60'
                                    }`}
                                  >
                                    <div className="flex items-start justify-between gap-3">
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                          {doc.access.is_active ? (
                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-success-50 dark:bg-success-900/20 text-success-700 dark:text-success-300 border border-success-200 dark:border-success-800">
                                              <CheckCircleIcon className="h-3 w-3" />
                                              Aktiv
                                            </span>
                                          ) : (
                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-error-50 dark:bg-error-900/20 text-error-700 dark:text-error-300 border border-error-200 dark:border-error-800">
                                              <XCircleIcon className="h-3 w-3" />
                                              Abgelaufen
                                            </span>
                                          )}
                                          <p className="font-semibold text-sm text-gray-900 dark:text-white truncate">{doc.name}</p>
                                        </div>
                                        {doc.tags.length > 0 && (
                                          <div className="flex flex-wrap gap-1 mt-1.5">
                                            {doc.tags.map((tag, idx) => (
                                              <span key={idx} className="text-[11px] px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded font-medium">
                                                {tag}
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                                          {doc.creator} · {formatSize(doc.size)} · {formatDate(doc.created_at)}
                                        </p>
                                      </div>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleRevokeSingleAccess(doc.id, memberDetails.member.mg_nr, doc.name);
                                        }}
                                        className="p-1.5 hover:bg-error-50 dark:hover:bg-error-900/20 rounded-lg transition-colors group flex-shrink-0"
                                        title="Recht entziehen"
                                      >
                                        <TrashIcon className="h-4 w-4 text-gray-400 group-hover:text-error-600 dark:group-hover:text-error-400" />
                                      </button>
                                    </div>
                                    {(doc.access.start_date || doc.access.expiration_date) && (
                                      <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700/50 flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                                        <ClockIcon className="h-3.5 w-3.5" />
                                        {doc.access.start_date && <span>Ab: {formatDate(doc.access.start_date)}</span>}
                                        {doc.access.expiration_date && <span>Bis: {formatDate(doc.access.expiration_date)}</span>}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Dog Documents List */}
                          {memberDetails.dog_documents.length > 0 && (
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <h5 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                                  <ChartBarIcon className="h-4 w-4" />
                                  Hundedokumente
                                </h5>
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 tabular-nums">
                                  {memberDetails.dog_documents.length}
                                </span>
                              </div>
                              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                                {memberDetails.dog_documents.map((doc) => (
                                  <div
                                    key={doc.id}
                                    className={`p-3 rounded-lg border bg-white dark:bg-[#1c2432] ${
                                      doc.access.is_active
                                        ? 'border-gray-200 dark:border-gray-700'
                                        : 'border-gray-200 dark:border-gray-700 opacity-60'
                                    }`}
                                  >
                                    <div className="flex items-start justify-between gap-3">
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                          {doc.access.is_active ? (
                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-info-50 dark:bg-info-900/20 text-info-700 dark:text-info-300 border border-info-200 dark:border-info-800">
                                              <CheckCircleIcon className="h-3 w-3" />
                                              Aktiv
                                            </span>
                                          ) : (
                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-error-50 dark:bg-error-900/20 text-error-700 dark:text-error-300 border border-error-200 dark:border-error-800">
                                              <XCircleIcon className="h-3 w-3" />
                                              Abgelaufen
                                            </span>
                                          )}
                                          <p className="font-semibold text-sm text-gray-900 dark:text-white truncate">{doc.name}</p>
                                        </div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                                          Hund: <span className="font-medium text-gray-700 dark:text-gray-300">{doc.dog_name}</span>
                                          {doc.type && <> · {doc.type}</>}
                                          {' · '}{formatDate(doc.created_at)}
                                        </p>
                                      </div>
                                    </div>
                                    {(doc.access.start_date || doc.access.expiration_date) && (
                                      <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700/50 flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                                        <ClockIcon className="h-3.5 w-3.5" />
                                        {doc.access.start_date && <span>Ab: {formatDate(doc.access.start_date)}</span>}
                                        {doc.access.expiration_date && <span>Bis: {formatDate(doc.access.expiration_date)}</span>}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {memberDetails.documents.length === 0 && memberDetails.dog_documents.length === 0 && (
                            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                              <DocumentTextIcon className="h-10 w-10 mx-auto mb-2 opacity-40" />
                              <p className="text-sm">Keine Dokumente vorhanden</p>
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
                );
              })}

              {filteredMembers.length === 0 && (
                <div className="text-center py-16 px-6">
                  <div className="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4">
                    <MagnifyingGlassIcon className="h-7 w-7 text-gray-400 dark:text-gray-500" />
                  </div>
                  <p className="text-base font-semibold text-gray-900 dark:text-white">Keine Mitglieder gefunden</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {searchTerm || filterRole !== 'all'
                      ? 'Passen Sie Suche oder Filter an, um andere Mitglieder zu sehen.'
                      : 'Es sind aktuell keine Mitglieder vorhanden.'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50/60 dark:bg-gray-800/40 border-t border-gray-200 dark:border-gray-700 px-6 py-3 flex items-center justify-between">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {!loading && stats && (
              <>
                <span className="font-medium text-gray-700 dark:text-gray-300">{filteredMembers.length}</span>
                {' '}von{' '}
                <span className="font-medium text-gray-700 dark:text-gray-300">{stats.total_members}</span>
                {' '}Mitglieder{filteredMembers.length !== stats.total_members ? ' (gefiltert)' : ''}
              </>
            )}
          </p>
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg transition-colors"
          >
            Schließen
          </button>
        </div>
      </div>

      {/* Confirmation Modal für Rechte-Entzug */}
      <ConfirmationModal
        visible={showRevokeConfirm}
        title="Recht entziehen"
        message={`Möchten Sie wirklich das Zugriffsrecht für "${revokeData?.documentName}" entziehen?\n\nDas Mitglied wird keinen Zugriff mehr auf dieses Dokument haben.`}
        type="warning"
        variant="two"
        buttons={{
          cancel: {
            text: 'Abbrechen',
            onPress: () => {
              setShowRevokeConfirm(false);
              setRevokeData(null);
            }
          },
          confirm: {
            text: 'Recht entziehen',
            onPress: confirmRevokeSingleAccess,
            loading: revokingDocument
          }
        }}
      />

    </div>
  );
};

export default DocumentAccessModal;
